import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import ReviewSection from '@/components/ReviewSection';
import { ArrowLeft, MapPin, Briefcase, Building2, Globe, Mail, Phone, Linkedin, Facebook, Instagram, FileText, Download, Star, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ViewProfile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const userId = searchParams.get('user');
  
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasConversation, setHasConversation] = useState(false);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });

  useEffect(() => {
    if (userId) {
      fetchProfile();
      if (currentUser && currentUser.id !== userId) {
        checkConversation();
        trackProfileView();
      }
    }
  }, [userId, currentUser]);

  const fetchProfile = async () => {
    try {
      const userRes = await axios.get(`${API}/users/${userId}`);
      setUser(userRes.data);

      const endpoint = userRes.data.user_type === 'professional'
        ? `/profiles/professional/${userId}`
        : `/profiles/company/${userId}`;
      
      const profileRes = await axios.get(`${API}${endpoint}`);
      setProfile(profileRes.data);
      
      // Fetch review stats for professionals
      if (userRes.data.user_type === 'professional') {
        try {
          const statsRes = await axios.get(`${API}/reviews/stats/${userId}`);
          setReviewStats(statsRes.data);
        } catch (e) {
          console.error('Error fetching review stats:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Profilo non trovato');
    } finally {
      setLoading(false);
    }
  };

  const checkConversation = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const res = await axios.get(`${API}/conversations/check/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasConversation(res.data.has_conversation);
    } catch (error) {
      console.error('Error checking conversation:', error);
    }
  };

  const trackProfileView = async () => {
    try {
      await axios.post(`${API}/analytics/profile-view/${userId}`);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleDownloadDocument = (url, filename) => {
    // For base64 data URLs
    if (url.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // For external URLs
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-8 mt-20">
          <Card className="p-12 text-center">
            <p className="text-slate-600">Profilo non trovato</p>
            <Button onClick={() => navigate(-1)} className="mt-4">Torna Indietro</Button>
          </Card>
        </div>
      </Layout>
    );
  }

  const isProfessional = user.user_type === 'professional';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8 mt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={() => navigate(-1)}
              variant="ghost"
              className="text-slate-600 hover:text-[#344B71]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Indietro
            </Button>
          </div>

          <Card className="p-8 bg-white">
            {/* Header */}
            <div className="flex items-start space-x-6 pb-6 border-b border-slate-200 mb-6">
              {isProfessional ? (
                profile.profile_image_url ? (
                  <img src={profile.profile_image_url} alt={user.name} className="w-24 h-24 rounded-full object-cover" />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center text-white text-3xl font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )
              ) : (
                profile.logo_url ? (
                  <img src={profile.logo_url} alt={profile.company_name} className="w-24 h-24 object-contain" />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-lg flex items-center justify-center">
                    <Building2 className="w-12 h-12 text-white" />
                  </div>
                )
              )}
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-3xl font-playfair font-bold text-[#344B71]">
                    {isProfessional ? user.name : profile.company_name}
                  </h1>
                  {/* Verified Badge */}
                  {isProfessional && profile.is_verified && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      <BadgeCheck className="w-4 h-4" />
                      Verificato
                    </span>
                  )}
                </div>
                
                {isProfessional && (
                  <>
                    <div className="flex items-center space-x-2 text-slate-600 mb-2">
                      <Briefcase className="w-4 h-4" />
                      <span>{profile.professional_category}</span>
                    </div>
                    
                    {/* Rating Summary */}
                    {reviewStats.total_reviews > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Star className="w-5 h-5 fill-[#E8A007] text-[#E8A007]" />
                          <span className="font-semibold text-[#344B71]">{reviewStats.average_rating.toFixed(1)}</span>
                        </div>
                        <span className="text-sm text-slate-500">
                          ({reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? 'recensione' : 'recensioni'})
                        </span>
                      </div>
                    )}
                  </>
                )}
                {!isProfessional && profile.contact_person && (
                  <p className="text-slate-600 mb-2">Referente: {profile.contact_person}</p>
                )}
              </div>
            </div>

            {/* Info */}
            {isProfessional ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Settori</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.sectors.map((sector, i) => (
                      <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {sector}
                      </span>
                    ))}
                  </div>
                </div>

                {profile.skills && profile.skills.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-[#344B71] mb-2">Competenze</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill, i) => (
                        <span key={i} className="px-3 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold text-[#344B71] mb-2">Esperienza</h3>
                  <p className="text-slate-700">{profile.experience_years} anni</p>
                </div>

                {/* Albo Professionale */}
                {profile.iscrizione_albo && (
                  <div>
                    <h3 className="text-lg font-semibold text-[#344B71] mb-2 flex items-center gap-2">
                      <BadgeCheck className="w-5 h-5 text-[#344B71]" />
                      Albo Professionale
                    </h3>
                    <p className="text-slate-700">{profile.albo_dettaglio}</p>
                  </div>
                )}

                {profile.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-[#344B71] mb-2">Descrizione</h3>
                    <p className="text-slate-700 leading-relaxed">{profile.description}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#344B71] mb-2">Settore</h3>
                  <p className="text-slate-700">{profile.sector}</p>
                </div>

                {profile.partita_iva && (
                  <div>
                    <h3 className="text-lg font-semibold text-[#344B71] mb-2">P.IVA / Codice Fiscale</h3>
                    <p className="text-slate-700 font-mono">{profile.partita_iva}</p>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold text-[#344B71] mb-2">Dimensione</h3>
                  <p className="text-slate-700">{profile.company_size} dipendenti</p>
                </div>

                {profile.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-[#344B71] mb-2">Descrizione</h3>
                    <p className="text-slate-700 leading-relaxed">{profile.description}</p>
                  </div>
                )}
              </div>
            )}

            {/* Contatti */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-[#344B71] mb-4">Contatti</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.email_contact && (
                  <a href={`mailto:${profile.email_contact}`} className="flex items-center space-x-2 text-slate-700 hover:text-[#344B71] transition-colors">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{profile.email_contact}</span>
                  </a>
                )}
                {profile.phone && (
                  <a href={`tel:${profile.phone}`} className="flex items-center space-x-2 text-slate-700 hover:text-[#344B71] transition-colors">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{profile.phone}</span>
                  </a>
                )}
                {profile.website && (
                  <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-slate-700 hover:text-[#344B71] transition-colors">
                    <Globe className="w-4 h-4" />
                    <span className="text-sm">Sito Web</span>
                  </a>
                )}
                {profile.linkedin_url && (
                  <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-slate-700 hover:text-[#344B71] transition-colors">
                    <Linkedin className="w-4 h-4" />
                    <span className="text-sm">LinkedIn</span>
                  </a>
                )}
                {profile.facebook_url && (
                  <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-slate-700 hover:text-[#344B71] transition-colors">
                    <Facebook className="w-4 h-4" />
                    <span className="text-sm">Facebook</span>
                  </a>
                )}
                {profile.instagram_url && (
                  <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-slate-700 hover:text-[#344B71] transition-colors">
                    <Instagram className="w-4 h-4" />
                    <span className="text-sm">Instagram</span>
                  </a>
                )}
              </div>
            </div>

            {/* CV / Company Profile Download - Only visible if has conversation */}
            {hasConversation && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#344B71] mb-4">
                  {isProfessional ? 'Curriculum Vitae' : 'Profilo Aziendale'}
                </h3>
                
                {isProfessional ? (
                  // Professional CV
                  profile.cv_url ? (
                    <div className="flex items-center justify-between p-4 bg-[#344B71]/5 rounded-xl border border-[#344B71]/20">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-[#344B71] rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-[#344B71]">CV di {user.name}</p>
                          <p className="text-sm text-slate-500">Documento disponibile</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownloadDocument(profile.cv_url, `CV_${user.name.replace(/\s+/g, '_')}.pdf`)}
                        className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Scarica CV
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50 rounded-xl text-center">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">Il professionista non ha ancora caricato il CV</p>
                    </div>
                  )
                ) : (
                  // Company Profile
                  profile.company_profile_url ? (
                    <div className="flex items-center justify-between p-4 bg-[#E8A007]/5 rounded-xl border border-[#E8A007]/20">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-[#E8A007] rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-[#344B71]">Profilo di {profile.company_name}</p>
                          <p className="text-sm text-slate-500">Documento disponibile</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDownloadDocument(profile.company_profile_url, `Profilo_${profile.company_name.replace(/\s+/g, '_')}.pdf`)}
                        className="bg-[#E8A007] hover:bg-[#E09612] text-white rounded-xl"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Scarica Profilo
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50 rounded-xl text-center">
                      <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">L&apos;azienda non ha ancora caricato il profilo aziendale</p>
                    </div>
                  )
                )}
              </div>
            )}
          </Card>
          
          {/* Reviews Section - Only for professionals */}
          {isProfessional && (
            <ReviewSection 
              professionalId={userId} 
              professionalName={user.name}
              canReview={hasConversation}
            />
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default ViewProfile;
